import type {
  Market,
  MarketFilters,
  OrderBook,
  OrderRequest,
  OrderResult,
  Position,
  TimeInForce,
} from "./types";
import {
  type Unsubscribe,
  type VenueAdapter,
  type VenueAuthState,
} from "./lib/http";
import {
  PolymarketClient,
  type ClobCredentials,
} from "./client";
import { mapClobBook, mapGammaMarket } from "./mappers";
import {
  buildCTFOrder,
  signCTFOrder,
  type ClobOrderRequest,
  type ClobTimeInForce,
  type OrderSigner,
} from "./signing";

export type PolymarketConfig = {
  gammaUrl?: string;
  clobUrl?: string;
  /** Pre-derived CLOB credentials. If omitted, derived from `signer` lazily. */
  credentials?: ClobCredentials;
  /** EIP-712 signer (viem WalletClient or PrivateKeyAccount). Required for trading. */
  signer?: OrderSigner;
};

const VENUE = "polymarket" as const;

// sessionStorage key for the CLOB API-key trio. Per-tab, per-address: cleared
// when the tab closes or the user switches wallets.
const CREDS_KEY_PREFIX = "hh:polymarket:creds:";

function readCachedCreds(address: string): ClobCredentials | null {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return null;
    const raw = window.sessionStorage.getItem(
      `${CREDS_KEY_PREFIX}${address.toLowerCase()}`,
    );
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ClobCredentials>;
    if (!parsed.apiKey || !parsed.secret || !parsed.passphrase) return null;
    return {
      apiKey: parsed.apiKey,
      secret: parsed.secret,
      passphrase: parsed.passphrase,
    };
  } catch {
    return null;
  }
}

function writeCachedCreds(address: string, creds: ClobCredentials) {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return;
    window.sessionStorage.setItem(
      `${CREDS_KEY_PREFIX}${address.toLowerCase()}`,
      JSON.stringify(creds),
    );
  } catch {
    /* ignore quota / private-mode errors */
  }
}

function clearCachedCreds(address: string) {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return;
    window.sessionStorage.removeItem(
      `${CREDS_KEY_PREFIX}${address.toLowerCase()}`,
    );
  } catch {
    /* ignore */
  }
}

export type PolymarketAdapter = VenueAdapter & {
  /** Provide / replace the EIP-712 signer used for trading. */
  setSigner(signer: OrderSigner | null): void;
  /** Pre-derive (or set) the CLOB API key trio so first order doesn't double-prompt. */
  ensureCredentials(): Promise<ClobCredentials | null>;
};

export function createPolymarketAdapter(
  config: PolymarketConfig = {},
): PolymarketAdapter {
  const client = new PolymarketClient({
    gammaUrl: config.gammaUrl,
    clobUrl: config.clobUrl,
  });
  let auth: VenueAuthState = { ready: false };
  let signer: OrderSigner | null = config.signer ?? null;
  let credentials: ClobCredentials | null = config.credentials ?? null;

  // Hydrate from sessionStorage when the constructor is called with a signer
  // but no explicit credentials — saves the user a sig prompt within the tab.
  if (signer && !credentials) {
    credentials = readCachedCreds(signer.address);
  }
  if (signer && credentials) {
    client.setAuth(signer.address, credentials);
    auth = { ready: true, address: signer.address, apiKeys: true };
  } else if (signer) {
    auth = { ready: true, address: signer.address, apiKeys: false };
  }

  // negRisk gate is per-condition. Fetch once via Gamma and cache in memory.
  const negRiskCache = new Map<string, boolean>();

  async function resolveNegRisk(conditionIdOrSlug: string): Promise<boolean> {
    if (negRiskCache.has(conditionIdOrSlug)) {
      return negRiskCache.get(conditionIdOrSlug)!;
    }
    const m =
      (await client.getMarketByConditionId(conditionIdOrSlug)) ??
      (await client.getMarketBySlug(conditionIdOrSlug));
    const value = m?.negRisk ?? false;
    negRiskCache.set(conditionIdOrSlug, value);
    return value;
  }

  async function ensureCredentials(): Promise<ClobCredentials | null> {
    if (credentials) return credentials;
    if (!signer) return null;
    // Try sessionStorage first — same-tab subsequent orders skip the prompt.
    const cached = readCachedCreds(signer.address);
    if (cached) {
      credentials = cached;
      client.setAuth(signer.address, credentials);
      auth = { ready: true, address: signer.address, apiKeys: true };
      return credentials;
    }
    credentials = await client.createOrDeriveApiKey(signer);
    writeCachedCreds(signer.address, credentials);
    client.setAuth(signer.address, credentials);
    auth = { ready: true, address: signer.address, apiKeys: true };
    return credentials;
  }

  function tifFor(req: OrderRequest): ClobTimeInForce {
    const tif: TimeInForce | undefined = req.tif;
    if (tif === "fok" || tif === "frontend_market") return "FOK";
    if (tif === "ioc") return "IOC";
    if (tif === "gtc") return "GTC";
    // Default by order type: market → FOK, limit → GTC
    return req.type === "market" ? "FOK" : "GTC";
  }

  async function listMarkets(filters?: MarketFilters, limit = 100): Promise<Market[]> {
    const raws = await client.listMarkets({
      limit,
      tagSlug: filters?.category,
    });
    return raws.map(mapGammaMarket).filter((m): m is Market => m !== null);
  }

  return {
    venue: VENUE,
    async initialize() {},
    destroy() {},

    setSigner(next: OrderSigner | null) {
      const prevAddress = auth.address;
      signer = next;
      if (next === null) {
        credentials = null;
        auth = { ready: false };
        return;
      }
      // Address change → drop in-memory creds (the previous tenant's were for
      // a different wallet); the sessionStorage cache is keyed per-address so
      // we can re-hydrate if the user comes back to this address in this tab.
      if (
        prevAddress &&
        next.address.toLowerCase() !== prevAddress.toLowerCase()
      ) {
        credentials = null;
      }
      // Try the sessionStorage cache for the new address.
      if (!credentials) {
        const cached = readCachedCreds(next.address);
        if (cached) credentials = cached;
      }
      if (credentials) {
        client.setAuth(next.address, credentials);
        auth = { ready: true, address: next.address, apiKeys: true };
      } else {
        auth = { ready: true, address: next.address, apiKeys: false };
      }
    },

    ensureCredentials,

    markets: {
      list: listMarkets,
      get: async (venueMarketId: string): Promise<Market | null> => {
        const raw =
          (await client.getMarketByConditionId(venueMarketId)) ??
          (await client.getMarketBySlug(venueMarketId));
        return raw ? mapGammaMarket(raw) : null;
      },
      search: async (query: string, filters?: MarketFilters, limit = 20) => {
        const all = await listMarkets(filters, 200);
        const q = query.trim().toLowerCase();
        if (!q) return all.slice(0, limit);
        return all
          .filter(
            (m) =>
              m.question.toLowerCase().includes(q) ||
              (m.tags ?? []).some((t) => t.toLowerCase().includes(q)),
          )
          .slice(0, limit);
      },
    },

    depth: {
      fetchBook: async (tokenId: string): Promise<OrderBook> => {
        const raw = await client.fetchBook(tokenId);
        if (!raw) {
          return {
            marketId: `${VENUE}:${tokenId}`,
            tokenId,
            venue: VENUE,
            bids: [],
            asks: [],
            timestamp: new Date().toISOString(),
          };
        }
        return mapClobBook(tokenId, raw);
      },
      subscribeBook: (_tokenId: string, _cb: (book: OrderBook) => void): Unsubscribe => {
        return () => {};
      },
    },

    account: {
      fetchPositions: async (): Promise<Position[]> => [],
      subscribePositions: () => () => {},
    },

    trading: {
      placeOrder: async (req: OrderRequest): Promise<OrderResult> => {
        if (!signer) {
          return {
            success: false,
            error: "polymarket: no signer (connect a wallet first)",
          };
        }
        if (!req.tokenId) {
          return { success: false, error: "polymarket: tokenId required" };
        }
        if (!req.price) {
          return {
            success: false,
            error: "polymarket: explicit price required (use bestAsk/bestBid + slippage)",
          };
        }

        try {
          await ensureCredentials();
        } catch (e) {
          return {
            success: false,
            error: `polymarket: failed to derive API key — ${(e as Error).message}`,
          };
        }
        if (!credentials) {
          return { success: false, error: "polymarket: API credentials unavailable" };
        }

        // The marketId is "polymarket:{conditionId}" — strip the venue prefix.
        const conditionId = req.marketId.startsWith(`${VENUE}:`)
          ? req.marketId.slice(VENUE.length + 1)
          : req.marketId;
        let negRisk = false;
        try {
          negRisk = await resolveNegRisk(conditionId);
        } catch {
          negRisk = false;
        }

        const clobReq: ClobOrderRequest = {
          tokenId: req.tokenId,
          side: req.side === "buy" ? "BUY" : "SELL",
          price: req.price,
          size: req.size,
          timeInForce: tifFor(req),
        };

        try {
          const order = buildCTFOrder(clobReq, signer.address, signer.address);
          const signed = await signCTFOrder(order, signer, negRisk);
          const resp = await client.placeOrder(signed, clobReq);
          if (!resp.success) {
            return {
              success: false,
              error: resp.errorMsg ?? resp.status ?? "polymarket: order rejected",
            };
          }
          return {
            success: true,
            orderId: resp.orderID,
          };
        } catch (e) {
          return {
            success: false,
            error: `polymarket: ${(e as Error).message}`,
          };
        }
      },
      cancelOrder: async (orderId: string): Promise<OrderResult> => {
        try {
          const resp = await client.cancelOrder(orderId);
          return { success: resp.success };
        } catch (e) {
          return { success: false, error: `polymarket: ${(e as Error).message}` };
        }
      },
      getOrder: async () => null,
    },

    auth: {
      init: async () => {
        if (signer) {
          auth = {
            ready: true,
            address: signer.address,
            apiKeys: !!credentials,
          };
        } else {
          auth = { ready: false };
        }
        return auth;
      },
      state: () => auth,
      clear: () => {
        if (signer) clearCachedCreds(signer.address);
        signer = null;
        credentials = null;
        auth = { ready: false };
      },
    },
  };
}
